<template>
    <v-container pa-0>
        <v-card class="pt-2 px-2 pb-4 mt-3" flat tile>

            <v-expand-transition>
                <div v-if="!status"
                     class="d-flex flex-row align-center">
                    <back-button/>
                </div>
            </v-expand-transition>

            <div class="px-2 mt-2">
                <template v-if="!canClaimContracts">
                    <h1 class="headline font-weight-bold mb-2">{{ $vuetify.lang.t('$vuetify.add_contract.headline') }}</h1>
                    <p v-html="$vuetify.lang.t('$vuetify.add_contract.no_permission')"></p>
                    <p v-html="$vuetify.lang.t('$vuetify.add_contract.no_permission_additional')"></p>
                </template>
                <template v-else-if="incompleteProfile">
                    <h1 class="headline font-weight-bold mb-2">{{ $vuetify.lang.t('$vuetify.add_contract.incomplete_profile_headline') }}</h1>
                    <p v-html="$vuetify.lang.t('$vuetify.add_contract.incomplete_profile')"></p>
                    <v-btn color="primary"
                           :disabled="whileSubmitting || !valid"
                           :loading="whileSubmitting"
                           :to="changeDataUrl">
                        {{ $vuetify.lang.t('$vuetify.buttons.add_personal_data') }}
                    </v-btn>
                </template>
                <template v-else-if="status">
                    <h1 class="headline font-weight-bold mb-2">{{ $vuetify.lang.t('$vuetify.add_contract.headline') }}</h1>
                    <submit-contract-result
                            class="mb-3"
                            :user="currentUser"
                            :is-instant-approved="isInstantApproved"
                            :success="success"
                            :balance_increase="balance_increase"
                            :current_balance="current_balance"
                            :contract="contract"
                            :errors="formattedErrors"/>

                    <div class="d-flex">
                        <v-spacer></v-spacer>

                        <v-btn v-if="formattedErrors.length > 0"
                               color="primary"
                               @click="editForm">
                            {{ $vuetify.lang.t('$vuetify.buttons.verify_data') }}
                        </v-btn>
                        <v-btn v-if="success"
                               color="primary"
                               @click="resetForm">
                            {{ $vuetify.lang.t('$vuetify.buttons.submit_another_contract') }}
                        </v-btn>
                    </div>
                </template>
                <template v-else>
                    <h1 class="headline font-weight-bold mb-2">{{ $vuetify.lang.t('$vuetify.add_contract.headline') }}</h1>
                    <p>
                        {{ $vuetify.lang.t('$vuetify.add_contract.desc') }}
                    </p>
                    <v-form ref="form"
                            v-model="valid"
                            lazy-validation>

                        <!-- autocomplete -->
                        <v-autocomplete v-if="this.currentUser && this.currentUser.profile && this.currentUser.profile.employment_type_raw === 'trade-fair-representative'"
                                        required
                                        name="company_number"
                                        filled
                                        :items="transformedCompanies"
                                        :label="$vuetify.lang.t('$vuetify.form_items.company_id')"
                                        v-model="form.company_number"
                                        :hint="$vuetify.lang.t('$vuetify.form_items.company_id_hint')"
                        ></v-autocomplete>

                        <v-text-field name="ab_number"
                                      filled
                                      :label="$vuetify.lang.t('$vuetify.form_items.ab_number')"
                                      v-model="form.ab_number"
                                      :hint="$vuetify.lang.t('$vuetify.form_items.ab_number_hint')"
                                      required></v-text-field>

                        <v-select name="payment_method"
                                  filled
                                  v-model="form.payment_type"
                                  item-text="displayText"
                                  item-value="value"
                                  :items="payment_methods"
                                  :label="$vuetify.lang.t('$vuetify.form_items.payment_method')"></v-select>

                        <v-dialog ref="dialog"
                                  v-model="dateModal"
                                  :return-value.sync="form.potential_delivery_date"
                                  persistent
                                  width="290px"
                                  max-width="100%">
                            <template v-slot:activator="{ on }">
                                <v-text-field :value="form.potential_delivery_date | date"
                                              :label="$vuetify.lang.t('$vuetify.form_items.potential_delivery_date')"
                                              readonly
                                              filled
                                              :hint="$vuetify.lang.t('$vuetify.form_items.delivery_date-hint')"
                                              persistent-hint
                                              v-on="on"></v-text-field>
                            </template>
                            <v-date-picker v-model="form.potential_delivery_date"
                                           scrollable
                                           header-color="primary darken-1"
                                           color="white accent"
                                           full-width
                                           :locale="this.$i18n.locale">
                                <v-spacer></v-spacer>
                                <v-btn text color="secondary" @click="dateModal = false">
                                    {{ $vuetify.lang.t('$vuetify.buttons.cancel') }}
                                </v-btn>
                                <v-btn text color="primary" @click="$refs.dialog.save(form.potential_delivery_date)">
                                    {{ $vuetify.lang.t('$vuetify.buttons.ok') }}
                                </v-btn>
                            </v-date-picker>
                        </v-dialog>

                        <v-select name="payment_method"
                                  filled
                                  v-model="form.customer_type"
                                  item-text="displayText"
                                  item-value="value"
                                  :items="client_types"
                                  :label="$vuetify.lang.t('$vuetify.form_items.client_type')"></v-select>

                        <v-text-field name="customer_zip_code"
                                      filled
                                      :label="$vuetify.lang.t('$vuetify.form_items.customer_zip_code')"
                                      v-model="form.customer_zip"
                                      required></v-text-field>

                        <v-textarea name="commission"
                                    filled
                                    :label="$vuetify.lang.t('$vuetify.form_items.commission_optional')"
                                    :hint="$vuetify.lang.t('$vuetify.form_items.commission-hint')"
                                    persistent-hint
                                    v-model="form.commission"></v-textarea>

                        <div class="d-flex">
                            <v-spacer></v-spacer>
                            <v-btn color="primary"
                                   :disabled="whileSubmitting || !valid"
                                   :loading="whileSubmitting"
                                   @click="submit">
                                {{ $vuetify.lang.t('$vuetify.buttons.review_and_submit_contract') }}
                            </v-btn>
                        </div>

                    </v-form>
                </template>
            </div>
        </v-card>

    </v-container>
</template>

<script>
    import { mapState } from 'vuex';
    // utils
    import {
        errorHandler,
        parseErrorStatus,
    } from '../../utils/api';
    import { LOADER_SUBMIT_CONTRACT, ROUTE_CHANGE_DATA } from '../../utils/constants';
    import {
        setPageTitle,
        transformArrayToAutoSelectValues
    } from '../../utils/helper';
    // models
    import User from '../../models/User';
    import Contract from '../../models/Contract';
    // components
    import SubmitContractResult from '../../components/SubmitContractResult';
    import BackButton from '../../components/BackButton';

    export default {
        name: 'add-contract',
        components: {
            BackButton,
            SubmitContractResult,
        },
        data() {
            return {
                status: null,
                balance_increase: null,
                current_balance: null,
                success: false,
                contract: {},
                errors: {},
                isInstantApproved: false,
                // form related
                valid: true,
                dateModal: false,
                payment_methods: [
                    {
                        displayText: this.$vuetify.lang.t('$vuetify.payment_method.cash'),
                        value: 'cash',
                    },
                    {
                        displayText: this.$vuetify.lang.t('$vuetify.payment_method.finance'),
                        value: 'finance',
                    },
                ],
                client_types: [
                    {
                        displayText: this.$vuetify.lang.t('$vuetify.client_type.private'),
                        value: 'private',
                    },
                    {
                        displayText: this.$vuetify.lang.t('$vuetify.client_type.company'),
                        value: 'company',
                    },
                ],
                form: {
                    company_number: '',
                    ab_number: '',
                    customer_type: '',
                    payment_type: '',
                    customer_zip: '',
                    potential_delivery_date: '',
                    commission: '',
                },
            }
        },
        computed: {
            ...mapState(['companies']),
            transformedCompanies() {
                return transformArrayToAutoSelectValues(this.companies);
            },
            changeDataUrl() {
                return { name: ROUTE_CHANGE_DATA }
            },
            currentUser() {
                return User.getCurrent();
            },
            whileSubmitting() {
                return this.$wait.waiting(LOADER_SUBMIT_CONTRACT);
            },
            incompleteProfile() {
                return !(this.currentUser.gender && this.currentUser.last_name && this.currentUser.first_name &&
                    this.currentUser.profile.private_address_street && this.currentUser.profile.private_address_number &&
                    this.currentUser.profile.private_address_zip && this.currentUser.profile.private_address_zip &&
                    this.currentUser.profile.private_address_city && this.currentUser.profile.birth_date &&
                    this.currentUser.profile.private_phone && this.currentUser.profile.business_phone && this.currentUser.profile.business_email);
            },
            canClaimContracts() {
                return User.getCurrent() ? User.getCurrent().can_claim_contracts : false;
            },
            formattedErrors() {
                if (!this.errors) {
                    return {}
                }
                const formattedErrors = [];
                Object.values(this.errors).forEach(e => formattedErrors.push(...e));
                return formattedErrors;
            },
        },
        methods: {
            async submit() {
                // remove the commission field of form if not filled
                if (this.form.commission === '') delete this.form.commission;

                let result = null;
                try {
                    result = await Contract.submission(this.form);
                } catch (e) {
                    this.errors = errorHandler(e);
                    this.status = parseErrorStatus(e);
                    window.scrollTo(0, 0);

                    this.$wait.end(LOADER_SUBMIT_CONTRACT);
                    return;
                }

                this.$wait.end(LOADER_SUBMIT_CONTRACT);

                const { data } = result;
                this.balance_increase = data.balance_increase;
                this.current_balance = data.current_balance;
                this.success = data.success;
                this.status = data.status;
                this.contract = data.contract;
                this.isInstantApproved = data.success && this.currentUser.role === 'owner' && this.form.payment_type !== 'finance';

                User.update({
                    id: this.currentUser.id,
                    wallet: {
                        id: this.currentUser.wallet_id,
                        balance: data.current_balance,
                    }
                });

                window.scrollTo(0, 0);
            },
            editForm() {
                this.status = null;
                this.errors = {};
            },
            resetForm() {
                this.balance_increase = null;
                this.current_balance = null;
                this.form.ab_number = '';
                this.form.customer_type = '';
                this.form.payment_type = '';
                this.form.customer_zip = '';
                this.form.company_number = '';
                this.form.potential_delivery_date = '';
                this.form.commission = '';
                this.success = false;
                this.isInstantApproved = false;
                this.status = null;
            },
        },
    }
</script>
